/**
 * AIMDv2 – AddUrlModal
 *
 * Modal dialog for adding a URL-based document source.
 * Fields: Title, URL, Context/instructions for processing.
 */

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import DosenConfirmModal from '../../../components/DosenConfirmModal';
import DosenInput from '../../../components/DosenInput';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';

interface AddUrlModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (title: string, url: string, instructions: string) => void;
}

const AddUrlModal: React.FC<AddUrlModalProps> = ({ open, onClose, onSubmit }) => {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [instructions, setInstructions] = useState('');

  const isValid = title.trim().length > 0 && url.trim().length > 0;

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit(title.trim(), url.trim(), instructions.trim());
    setTitle('');
    setUrl('');
    setInstructions('');
    onClose();
  };

  const handleClose = () => {
    setTitle('');
    setUrl('');
    setInstructions('');
    onClose();
  };

  return (
    <DosenConfirmModal open={open} onClose={handleClose} title="Add URL sources to this Program">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', py: 1 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: 'Inter',
            color: 'var(--Text-text-secondary, #B6B6B6)',
            lineHeight: '16px'
          }}
        >
          Share URLs to webpages or online resources that will help us understand your program.
        </Typography>

        <DosenInput
          label="Title*"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter a title..."
        />

        <DosenInput
          label="URL*"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://..."
        />

        <DosenInput
          label="Context or instructions for processing"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder='e.g. "Prioritize our sales guidelines on page 2"'
        />

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <DosenButton
            tone="cta"
            sizeVariant="sm"
            onClick={handleSubmit}
            disabled={!isValid}
            sx={{ minWidth: 192 }}
          >
            Add URL
          </DosenButton>
        </Box>
      </Box>
    </DosenConfirmModal>
  );
};

export default AddUrlModal;
